import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { DesktopModeProvider } from './contexts/DesktopModeContext';
import { SyncStatusFloating } from './components/SyncStatusIndicator';
import DesktopModeToggle from './components/DesktopModeToggle';
import { AppVersionText } from './config/version';
import PullToRefresh from './components/PullToRefresh';
import SecurityShield from './components/SecurityShield';
import InstallAppPrompt from './components/InstallAppPrompt';
import ImpersonationBanner from './components/ImpersonationBanner';
import OfflineBanner from './components/OfflineBanner';
import api from './services/api';
import Login from './pages/Login';
import KlantLogin from './pages/KlantLogin';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';
import { EmployeeAppNav } from './components/employee/EmployeeAppNav';

// Lazy-loaded pages (code-splitting → kleinere initiële bundle)
const ModernDashboard = lazy(() => import('./pages/ModernDashboard'));
const ClothingManagement = lazy(() => import('./pages/ClothingManagement'));
const StockManagement = lazy(() => import('./pages/StockManagement'));
const EmployeeManagement = lazy(() => import('./pages/EmployeeManagement'));
const HuisregelsBeheerPage = lazy(() => import('./pages/HuisregelsBeheerPage'));
const EmployeeDetailPage = lazy(() => import('./pages/EmployeeDetailPage'));
const RequestsManagement = lazy(() => import('./pages/RequestsManagement'));
const ReturnsManagement = lazy(() => import('./pages/ReturnsManagement'));
const AssignmentsPage = lazy(() => import('./pages/AssignmentsPage'));
const EmployeePortal = lazy(() => import('./pages/EmployeePortal'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const EmployeeDetailView = lazy(() => import('./pages/EmployeeDetailView'));
const ESignManagement = lazy(() => import('./pages/ESignManagement'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const QRLabelsPage = lazy(() => import('./pages/QRLabelsPage'));
const ScanPage = lazy(() => import('./pages/ScanPage'));
const CostOverview = lazy(() => import('./pages/CostOverview'));
// Klanten module pagina's
const KlantenPage = lazy(() => import('./pages/KlantenPage'));
const WerklijstenPage = lazy(() => import('./pages/WerklijstenPage'));
const OmzetPage = lazy(() => import('./pages/OmzetPage'));
const VoertuigQRPage = lazy(() => import('./pages/VoertuigQRPage'));
const VoertuigScanPage = lazy(() => import('./pages/VoertuigScanPage'));
const WerklijstDetailPage = lazy(() => import('./pages/WerklijstDetailPage'));
const OpdrachtenOverzicht = lazy(() => import('./pages/OpdrachtenOverzicht'));
const OpdrachtenDetailPage = lazy(() => import('./pages/OpdrachtenDetailPage'));
const FacturenOverzicht = lazy(() => import('./pages/FacturenOverzicht'));
const LosseFactuur = lazy(() => import('./pages/LosseFactuur'));
const CreditFactuur = lazy(() => import('./pages/CreditFactuur'));
const OnboardingFlow = lazy(() => import('./pages/OnboardingFlow'));
const MedewerkerOpdrachten = lazy(() => import('./pages/MedewerkerOpdrachten'));
// Klantenportaal pagina's
const KlantDashboard = lazy(() => import('./pages/KlantDashboard'));
// Admin Klant Preview
const AdminKlantPreview = lazy(() => import('./pages/AdminKlantPreview'));
// Handtekening pagina
const DigitaleHandtekening = lazy(() => import('./pages/DigitaleHandtekening'));
// Handboek pagina
const HandboekPage = lazy(() => import('./pages/HandboekPage'));
const MilieuDocumentenPage = lazy(() => import('./pages/MilieuDocumentenPage'));
const WashistorieOverzicht = lazy(() => import('./pages/WashistorieOverzicht'));
const AdminSchadeOverzicht = lazy(() => import('./pages/AdminSchadeOverzicht'));
const VoertuigControle = lazy(() => import('./pages/VoertuigControle'));
const VoertuigControleTemplate = lazy(() => import('./pages/VoertuigControleTemplate'));
const FilterreinigingPage = lazy(() => import('./pages/FilterreinigingPage'));
const FilterreinigingAdmin = lazy(() => import('./pages/FilterreinigingAdmin'));
const VoertuigenBeheerOverzicht = lazy(() => import('./pages/VoertuigenBeheerOverzicht'));
const AfwezighedenPage = lazy(() => import('./pages/AfwezighedenPage'));
const ActiepuntenPage = lazy(() => import('./pages/ActiepuntenPage'));
const AdminActiepunten = lazy(() => import('./pages/AdminActiepunten'));
const ServiceAanvragenPage = lazy(() => import('./pages/ServiceAanvragenPage'));
const AcademyBeheer = lazy(() => import('./pages/AcademyBeheer'));
const AcademyDashboard = lazy(() => import('./pages/AcademyDashboard'));
const MijnAcademy = lazy(() => import('./pages/MijnAcademy'));
const MentorAcademy = lazy(() => import('./pages/MentorAcademy'));
const PlanningPage = lazy(() => import('./pages/PlanningPage'));
const LocatieInfoPage = lazy(() => import('./pages/LocatieInfoPage'));
const SecurityAudit = lazy(() => import('./pages/SecurityAudit'));
// Voertuigregistratie (zelfstandige module)
const VoertuigregistratieAdmin = lazy(() => import('./pages/VoertuigregistratieAdmin'));
const VoertuigregistratieMedewerker = lazy(() => import('./pages/VoertuigregistratieMedewerker'));
// Offertes
const OffertesPage = lazy(() => import('./pages/OffertesPage'));
const OfferteLandingPage = lazy(() => import('./pages/OfferteLandingPage'));
// Sales (Leads + Offertes gecombineerd overzicht)
const SalesPage = lazy(() => import('./pages/SalesPage'));
const SalesMailPage = lazy(() => import('./pages/SalesMailPage'));
// Prepaid — klant tegoed
const PrepaidPage = lazy(() => import('./pages/PrepaidPage'));

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(parsed);
        // Valideer token door /api/auth/me aan te roepen
        api.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`)
          .then(res => {
            // Token is geldig, update user data met verse info
            if (res.data) {
              const freshUser = res.data;
              localStorage.setItem('user', JSON.stringify(freshUser));
              setUser(freshUser);
            }
          })
          .catch(() => {
            // Token ongeldig/verlopen - verwijder en redirect
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          });
      } catch {
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    // BELANGRIJK: Clear klant sessie om conflict te voorkomen
    // Een gebruiker kan niet tegelijk medewerker EN klant zijn in dezelfde browser sessie
    localStorage.removeItem('klant_token');
    localStorage.removeItem('klant_user');
    localStorage.removeItem('klant_naam');
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Ruim ook eventuele impersonatie-backup op zodat we niet onbedoeld
    // bij een volgende login terug "gepushed" worden naar een oude admin-sessie
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('admin_user_backup');
    setUser(null);
  };

  const handleUserUpdate = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  if (loading) {
    return <div className="loading-screen">Laden...</div>;
  }

  return (
    <ErrorBoundary>
    <DesktopModeProvider>
      <BrowserRouter>
        <Toaster position="top-right" />
      <ImpersonationBanner user={user} />
      <OfflineBanner />
      <SecurityShield user={user}>
      <Suspense fallback={<div className="loading-screen">Laden...</div>}>
      <Routes>
        <Route
          path="/login"
          element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/klant/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/"
          element={
            user ? (
              user.role === 'admin' ? (
                <Navigate to="/admin/dashboard" />
              ) : (
                <Navigate to="/employee/portal" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            user?.role === 'admin' ? (
              <ModernDashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/clothing"
          element={
            user?.role === 'admin' ? (
              <ClothingManagement user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/stock"
          element={
            user?.role === 'admin' ? (
              <StockManagement user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/costs"
          element={
            user?.role === 'admin' ? (
              <CostOverview user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/accounts"
          element={
            user?.role === 'admin' ? (
              <EmployeeManagement user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/accounts/:id"
          element={
            user?.role === 'admin' ? (
              <EmployeeDetailPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/employees"
          element={
            <Navigate to="/admin/accounts" replace />
          }
        />
        <Route
          path="/admin/huisregels"
          element={
            user?.role === 'admin' ? (
              <HuisregelsBeheerPage />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* Voertuigregistratie (zelfstandige module) */}
        <Route
          path="/admin/voertuigregistratie"
          element={
            user?.role === 'admin' ? (
              <VoertuigregistratieAdmin user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/employee/voertuigregistratie"
          element={
            user?.role === 'employee' ? (
              <VoertuigregistratieMedewerker user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/employees/:employeeId"
          element={
            user?.role === 'admin' ? (
              <EmployeeDetailView user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/requests"
          element={
            user?.role === 'admin' ? (
              <RequestsManagement user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/returns"
          element={
            user?.role === 'admin' ? (
              <ReturnsManagement user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/assignments"
          element={
            user?.role === 'admin' ? (
              <AssignmentsPage user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/history"
          element={
            user?.role === 'admin' ? (
              <HistoryPage user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/esign"
          element={
            user?.role === 'admin' ? (
              <ESignManagement user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/settings"
          element={
            user?.role === 'admin' ? (
              <SettingsPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/employee/portal"
          element={
            user?.role === 'employee' ? (
              user?.onboarding_required ? (
                <Navigate to="/employee/onboarding" />
              ) : (
                <EmployeePortal user={user} onLogout={handleLogout} />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/employee/onboarding"
          element={
            user?.role === 'employee' ? (
              <OnboardingFlow user={user} onComplete={() => {
                const updatedUser = { ...user, onboarding_required: false };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
              }} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/employee/opdrachten"
          element={
            user?.role === 'employee' ? (
              <MedewerkerOpdrachten user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/employee/voertuig-controle"
          element={
            user?.role === 'employee' ? (
              <VoertuigControle />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/voertuig-controle-template"
          element={
            user?.role === 'admin' ? (
              <VoertuigControleTemplate user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/service-aanvragen"
          element={
            user?.role === 'admin' ? (
              <ServiceAanvragenPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/afwezigheden"
          element={user?.role === 'admin' ? <AfwezighedenPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/voertuigen-beheer"
          element={
            user?.role === 'admin' ? (
              <VoertuigenBeheerOverzicht user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/employee/filterreiniging"
          element={
            user?.role === 'employee' ? (
              <FilterreinigingPage />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/employee/actiepunten"
          element={
            user ? (
              (user.email === 'jerry@wasz.nl' || user.role === 'admin') ? (
                <ActiepuntenPage />
              ) : (
                <Navigate to="/employee/portal" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/filterreiniging"
          element={
            user?.role === 'admin' ? (
              <FilterreinigingAdmin user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/actiepunten"
          element={
            user?.role === 'admin' ? (
              <AdminActiepunten user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/handtekening"
          element={
            user ? (
              <DigitaleHandtekening user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/qr-labels"
          element={
            user?.role === 'admin' ? (
              <QRLabelsPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* Klanten module routes */}
        <Route
          path="/admin/klanten"
          element={
            user?.role === 'admin' ? (
              <KlantenPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/klant-preview/:klantId"
          element={
            user?.role === 'admin' ? (
              <AdminKlantPreview />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/werklijsten"
          element={
            (user?.role === 'admin' || user?.can_access_werklijsten) ? (
              <WerklijstenPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/werklijsten/:klantId/:werklijstId"
          element={
            (user?.role === 'admin' || user?.can_access_werklijsten) ? (
              <WerklijstDetailPage user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/opdrachten/:klantId"
          element={
            user?.role === 'admin' ? (
              <OpdrachtenOverzicht user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/opdrachten/:klantId/:opdrachtId"
          element={
            user?.role === 'admin' ? (
              <OpdrachtenDetailPage user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/facturatie"
          element={
            user?.role === 'admin' ? (
              <FacturenOverzicht user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/losse-factuur"
          element={
            user?.role === 'admin' ? (
              <LosseFactuur user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/credit-factuur"
          element={
            user?.role === 'admin' ? (
              <CreditFactuur user={user} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/omzet"
          element={
            user?.role === 'admin' ? (
              <OmzetPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/voertuig-qr"
          element={
            user?.role === 'admin' ? (
              <VoertuigQRPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/admin/washistorie"
          element={
            user?.role === 'admin' ? (
              <WashistorieOverzicht user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/scan/:employeeId"
          element={
            <ScanPage user={user} onLogin={(userData) => {
              localStorage.setItem('user', JSON.stringify(userData));
              setUser(userData);
            }} />
          }
        />
        <Route
          path="/scan/voertuig/:voertuigId"
          element={
            <VoertuigScanPage user={user} onLogin={(userData) => {
              localStorage.setItem('user', JSON.stringify(userData));
              setUser(userData);
            }} />
          }
        />
        {/* Klantenportaal routes */}
        <Route path="/klant/login" element={<KlantLogin />} />
        <Route path="/klant/dashboard" element={<KlantDashboard />} />
        <Route path="/klant" element={<Navigate to="/klant/login" />} />
        
        {/* Handboek route - publiek toegankelijk */}
        <Route path="/handboek" element={<HandboekPage />} />
        <Route path="/milieu-documenten" element={
          (user?.role === 'admin' || user?.milieu_docs_toegang)
            ? <MilieuDocumentenPage user={user} onBack={() => window.history.back()} isAdmin={user?.role === 'admin'} />
            : <Navigate to="/employee/portal" />
        } />
        <Route path="/admin/schade" element={<AdminSchadeOverzicht />} />
        <Route path="/admin/academy" element={<AcademyBeheer />} />
        <Route path="/admin/academy-dashboard" element={<AcademyDashboard />} />
        <Route path="/mentor/academy" element={<MentorAcademy />} />
        <Route path="/planning" element={user ? <PlanningPage user={user} /> : <Navigate to="/login" />} />
        <Route path="/locatie-informatie" element={user ? <LocatieInfoPage user={user} /> : <Navigate to="/login" />} />
        <Route path="/mijn-academy" element={<MijnAcademy />} />
        <Route
          path="/admin/security"
          element={user?.role === 'admin' ? <SecurityAudit /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/offertes"
          element={(user?.role === 'admin' || user?.can_access_offertes) ? <OffertesPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/klanten/:klantId/prepaid"
          element={user?.role === 'admin' ? <PrepaidPage user={user} /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/sales"
          element={(user?.role === 'admin' || user?.can_access_offertes) ? <SalesPage user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/sales/mail"
          element={(user?.role === 'admin' || user?.can_access_offertes) ? <SalesMailPage user={user} /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/leads"
          element={<Navigate to="/admin/sales" replace />}
        />
        {/* Publieke landingspage voor offerte (geen auth) */}
        <Route path="/offerte/:token" element={<OfferteLandingPage />} />
      </Routes>
      </Suspense>
      </SecurityShield>

      {user?.role === 'employee' && (
        <div className="py-4 pb-8 text-center text-slate-400 text-xs" data-testid="employee-version-footer">
          WaszBase v<AppVersionText />
        </div>
      )}
      
      {/* PWA install-prompt — eigen banner die elke sessie terugkomt tot installatie */}
      <InstallAppPrompt user={user} />
      
      {/* Globale Offline Sync Status Indicator - zichtbaar op alle pagina's */}
      <SyncStatusFloating />
      
      <EmployeeAppNav user={user} onLogout={handleLogout} />

      {/* Globale Desktop/Mobiel Toggle - links onderin */}
      {user?.role !== 'employee' && <DesktopModeToggle />}

      {/* Trek-om-te-vernieuwen voor iPhone en geïnstalleerde app */}
      <PullToRefresh />
    </BrowserRouter>
    </DesktopModeProvider>
    </ErrorBoundary>
  );
}

export default App;